import { ChevronRight, Plus } from "lucide-react";
import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";
import type { CanonicalSummary } from "@/lib/api-canonical";

export interface CanonicalListPanelProps {
  items: CanonicalSummary[];
  selected: { canonical_type: string; version: number } | null;
  onSelect: (type: string, version: number) => void;
  onNew: () => void;
  canEdit: boolean;
  /** Optional slot rendered under the header — used for the tag filter. */
  tagFilterSlot?: React.ReactNode;
}

export function CanonicalListPanel({
  items,
  selected,
  onSelect,
  onNew,
  canEdit,
  tagFilterSlot,
}: CanonicalListPanelProps) {
  const grouped = React.useMemo(() => {
    const map = new Map<string, CanonicalSummary[]>();
    for (const it of items) {
      const arr = map.get(it.canonical_type) ?? [];
      arr.push(it);
      map.set(it.canonical_type, arr);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => b.version - a.version);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [items]);

  return (
    <div className="flex h-full w-44 sm:w-56 md:w-72 flex-col border-r border-border bg-surface shrink-0">
      <div className="flex h-10 items-center justify-between border-b border-border px-3">
        <span className="text-xs uppercase tracking-wider text-muted-foreground">
          Canonical types
        </span>
        {canEdit && (
          <Button size="icon" variant="ghost" onClick={onNew} aria-label="New canonical">
            <Plus className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        )}
      </div>

      {tagFilterSlot && (
        <div className="border-b border-border px-3 py-2">{tagFilterSlot}</div>
      )}

      <div className="flex-1 overflow-y-auto">
        {grouped.length === 0 && (
          <div className="p-4 text-xs text-muted-foreground">No canonical types.</div>
        )}
        {grouped.map(([type, versions]) => (
          <div key={type}>
            <div className="px-3 pt-3 pb-1 text-xs uppercase tracking-wider text-muted-foreground font-mono">
              {type}
            </div>
            {versions.map((v) => {
              const isActive =
                selected?.canonical_type === v.canonical_type && selected?.version === v.version;
              return (
                <button
                  key={v.version}
                  onClick={() => onSelect(v.canonical_type, v.version)}
                  className={cn(
                    "group flex w-full items-center gap-2 px-3 h-8 text-sm text-left",
                    "border-l-2 border-transparent",
                    "hover:bg-muted",
                    isActive && "bg-muted border-accent",
                  )}
                >
                  <ChevronRight
                    className={cn(
                      "h-3 w-3 text-muted-foreground transition-transform",
                      isActive && "rotate-90 text-foreground",
                    )}
                    strokeWidth={1.75}
                  />
                  <span className="font-mono text-xs">v{v.version}</span>
                  <Badge tone={v.status as never} className="ml-auto">
                    {v.status}
                  </Badge>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
